import { useEffect, useState, useRef, RefObject } from 'react';
import { Input } from '~components/dumbs/Input';
import ScrollingList from '~components/dumbs/ScrollingList';
import { useOutsideClick, useKeydown, useViewportPosition } from '~hooks';
import * as Styled from './styled';
import { Props, Option } from './types';
import withField from '~hocs/withField';

function Dropdown({
  options,
  value,
  onChange,
  id,
  name,
  disabled,
  variant,
  placeholder,
  onSelect,
  ...rest
}: Props) {
  const [selected, setSelected] = useState<Option>({ value: '', id: '' });
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapperRef as RefObject<HTMLElement>, () => setIsOpen(false));
  useKeydown('Escape', () => setIsOpen(false));
  const { isOnFirstVerticalHalf } = useViewportPosition(wrapperRef);

  const handleSelect = (selected: Option) => {
    setIsOpen(false);
    onSelect?.(selected);
    setSelected(selected);
    onChange?.({ target: { id, name, value: selected.id } });
  };

  const handleClick = () => !disabled && setIsOpen((isOpen) => !isOpen);

  useEffect(() => {
    const found = options.find((item) => item.id === value) || {
      id: 0,
      value: '',
    };

    setSelected(found);
  }, [value, options]);

  return (
    <Styled.Wrapper ref={wrapperRef}>
      <Input
        name={name}
        id={name}
        value={selected?.value}
        onClick={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
      <Styled.ChevronDown $rotate={isOpen} onClick={handleClick} />
      {isOpen && (
        <Styled.Positioner position={isOnFirstVerticalHalf ? 'bottom' : 'top'}>
          <ScrollingList
            maxHeight="md"
            items={options}
            onClick={handleSelect}
          />
        </Styled.Positioner>
      )}
    </Styled.Wrapper>
  );
}

export default withField(Dropdown);
